import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { useState } from 'react';

import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { ConfirmDialog } from '@/components/ConfirmDialog/ConfirmDialog';
import { DimensionForm, type DimensionDraft } from '@/components/DimensionForm/DimensionForm';
import { DimensionList } from '@/components/DimensionList/DimensionList';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { Select } from '@/components/Select/Select';
import { Spinner } from '@/components/Spinner/Spinner';
import {
  CONFIGURATION_IDS_QUERY,
  CONFIGURATION_QUERY,
  CREATE_CASE_CONFIGURATION,
  CREATE_TASK_CONFIGURATION,
  DIMENSIONS,
  type ConfigurationIdsResult,
  type ConfigurationResult,
  type Dimension,
  type DimensionKind,
} from '@/graphql/brokerage-settings';
import { stepOrder } from '@/lib/brokerages';
import { friendlyError, isPermissionDenied } from '@/lib/keystone-errors';
import { useToast } from '@/toast/useToast';

import styles from '../BrokeragesContainer.module.css';
import type { SectionProps } from './MembersSection';

interface Target {
  kind: DimensionKind;
  configurationId: string;
  items: Dimension[];
}

function outcomeFields(draft: DimensionDraft) {
  if (!draft.outcome) {
    return {};
  }
  return {
    color: draft.color ?? null,
    isTerminalSuccess: draft.outcome === 'SUCCESS',
    isTerminalFailure: draft.outcome === 'FAILURE',
  };
}

// GraphQL coerces every variable, so a skipped list still needs a well-formed id.
const NO_CONFIGURATION = '00000000-0000-0000-0000-000000000000';

const byPosition = (items: readonly Dimension[]) =>
  [...items].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

/** Case stages and sources, task types and statuses: what the main app's pipelines are built from. */
export function ConfigurationSection({ tenantId, canEdit, onAccessChanged }: SectionProps) {
  const toast = useToast();
  const ids = useQuery<ConfigurationIdsResult>(CONFIGURATION_IDS_QUERY, {
    variables: { tenantId },
    fetchPolicy: 'cache-and-network',
  });
  const caseConfigurations = ids.data?.brokerage.caseConfigurations ?? [];
  const taskConfigurations = ids.data?.brokerage.taskConfigurations ?? [];
  const caseConfigurationId = caseConfigurations[0]?.id ?? null;
  const taskConfigurationId = taskConfigurations[0]?.id ?? null;
  const config = useQuery<ConfigurationResult>(CONFIGURATION_QUERY, {
    variables: {
      caseConfigurationId: caseConfigurationId ?? NO_CONFIGURATION,
      taskConfigurationId: taskConfigurationId ?? NO_CONFIGURATION,
      hasCase: caseConfigurationId !== null,
      hasTask: taskConfigurationId !== null,
    },
    skip: !ids.data || (caseConfigurationId === null && taskConfigurationId === null),
    fetchPolicy: 'cache-and-network',
  });
  const client = useApolloClient();
  const [createCaseConfiguration] = useMutation(CREATE_CASE_CONFIGURATION);
  const [createTaskConfiguration] = useMutation(CREATE_TASK_CONFIGURATION);

  const [form, setForm] = useState<{ target: Target; item: Dimension | null } | null>(null);
  const [deleting, setDeleting] = useState<{ target: Target; item: Dimension } | null>(null);
  const [fallbackId, setFallbackId] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = (
    mutation: (typeof DIMENSIONS)[DimensionKind]['create'],
    variables: Record<string, unknown>,
  ) => client.mutate({ mutation, variables });

  const run = async (call: () => Promise<unknown>, success: string | null) => {
    setBusy(true);
    setError(null);
    try {
      await call();
      await ids.refetch();
      await config.refetch();
      if (success) {
        toast.success({ title: success });
      }
      return true;
    } catch (cause) {
      setError(friendlyError(cause));
      if (isPermissionDenied(cause)) {
        onAccessChanged();
      }
      return false;
    } finally {
      setBusy(false);
      setBusyId(null);
    }
  };

  const onSubmit = async (draft: DimensionDraft) => {
    if (!form) {
      return;
    }
    const { target, item } = form;
    const spec = DIMENSIONS[target.kind];
    const fields = { label: draft.label, description: draft.description, ...outcomeFields(draft) };
    const ok = await run(
      () =>
        item
          ? mutate(spec.update, { input: { id: item.id, ...fields } })
          : mutate(spec.create, {
              input: { [spec.parentField]: target.configurationId, ...fields },
            }),
      item ? `${draft.label} saved` : `${draft.label} added`,
    );
    if (ok) {
      setForm(null);
    }
  };

  const onMove = (target: Target, item: Dimension, direction: -1 | 1) => {
    const ids = stepOrder(
      byPosition(target.items).map((row) => row.id),
      item.id,
      direction,
    );
    if (!ids) {
      return;
    }
    setBusyId(item.id);
    void run(
      () =>
        mutate(DIMENSIONS[target.kind].reorder, {
          input: { configurationId: target.configurationId, orderedIds: ids },
        }),
      null,
    );
  };

  const onDelete = async () => {
    if (!deleting) {
      return;
    }
    const { target, item } = deleting;
    const ok = await run(
      () => mutate(DIMENSIONS[target.kind].remove, { id: item.id, fallbackId: fallbackId || null }),
      `${item.label ?? 'It'} deleted`,
    );
    if (ok) {
      setDeleting(null);
    }
  };

  const list = (kind: DimensionKind, configurationId: string, items: Dimension[]) => {
    const spec = DIMENSIONS[kind];
    const target = { kind, configurationId, items };
    return (
      <DimensionList
        busyId={busyId}
        canEdit={canEdit}
        hasOutcome={spec.hasOutcome}
        items={items}
        key={`${kind}-${configurationId}`}
        onAdd={() => {
          setError(null);
          setForm({ target, item: null });
        }}
        onDelete={(item) => {
          setError(null);
          setFallbackId(byPosition(items).find((row) => row.id !== item.id)?.id ?? '');
          setDeleting({ target, item });
        }}
        onEdit={(item) => {
          setError(null);
          setForm({ target, item });
        }}
        onMove={(item, direction) => onMove(target, item, direction)}
        singular={spec.singular}
        title={spec.title}
      />
    );
  };

  const needsLists = caseConfigurationId !== null || taskConfigurationId !== null;
  const loaded = Boolean(ids.data) && (!needsLists || Boolean(config.data));
  const failure = ids.data ? config.error : ids.error;
  if (!loaded && failure) {
    return (
      <Callout tone="danger" title="Setup couldn't be loaded">
        {friendlyError(failure)}
      </Callout>
    );
  }
  if (!loaded) {
    return (
      <p className={styles.loading} role="status">
        <Spinner /> Loading setup…
      </p>
    );
  }

  const extra = (count: number, kind: string) =>
    count > 1 ? (
      <Callout title={`This brokerage has ${count} ${kind} configurations`}>
        The main app uses the oldest one, shown here. The others are unused.
      </Callout>
    ) : null;
  const startButton = (label: string, create: () => Promise<unknown>) =>
    canEdit ? (
      <Button loading={busy} onClick={() => void run(create, null)}>
        {label}
      </Button>
    ) : undefined;

  return (
    <section aria-label="Case and task setup" className={styles.section}>
      <p className={styles.sectionLede}>
        The stages a case moves through, where cases come from, and how tasks are sorted. The main
        app reads these straight from Keystone, so changes show there at once.
      </p>
      {error && !form && !deleting ? (
        <Callout tone="danger" title="That didn't work">
          {error}
        </Callout>
      ) : null}
      <div className={styles.configs}>
        <div className={styles.configGroup}>
          <h2 className={styles.configTitle}>Cases</h2>
          {extra(caseConfigurations.length, 'case')}
          {caseConfigurationId === null ? (
            <EmptyState
              action={startButton('Set up cases', () =>
                createCaseConfiguration({ variables: { tenantId } }),
              )}
              icon="file"
              title="Cases aren't set up for this brokerage"
            />
          ) : (
            <div className={styles.configGroup}>
              {list('caseStatus', caseConfigurationId, config.data?.caseStatuses ?? [])}
              {list('caseSource', caseConfigurationId, config.data?.caseSources ?? [])}
            </div>
          )}
        </div>
        <div className={styles.configGroup}>
          <h2 className={styles.configTitle}>Tasks</h2>
          {extra(taskConfigurations.length, 'task')}
          {taskConfigurationId === null ? (
            <EmptyState
              action={startButton('Set up tasks', () =>
                createTaskConfiguration({ variables: { tenantId } }),
              )}
              icon="check-circle"
              title="Tasks aren't set up for this brokerage"
            />
          ) : (
            <div className={styles.configGroup}>
              {list('taskType', taskConfigurationId, config.data?.taskTypes ?? [])}
              {list('taskStatus', taskConfigurationId, config.data?.taskStatuses ?? [])}
            </div>
          )}
        </div>
      </div>

      {form ? (
        <DimensionForm
          busy={busy}
          error={error}
          hasOutcome={DIMENSIONS[form.target.kind].hasOutcome}
          item={form.item}
          onCancel={() => setForm(null)}
          onSubmit={onSubmit}
          singular={DIMENSIONS[form.target.kind].singular}
        />
      ) : null}

      {deleting ? (
        <ConfirmDialog
          busy={busy}
          confirmLabel="Delete"
          error={error}
          onCancel={() => setDeleting(null)}
          onConfirm={onDelete}
          title={`Delete ${deleting.item.label ?? 'this'}?`}
        >
          <p>Anything using it now moves to the one you choose below.</p>
          <Select
            label="Move them to"
            onChange={setFallbackId}
            options={byPosition(deleting.target.items)
              .filter((row) => row.id !== deleting.item.id)
              .map((row) => [row.id, row.label ?? row.key ?? row.id] as const)}
            value={fallbackId}
          />
        </ConfirmDialog>
      ) : null}
    </section>
  );
}
